import React, { useState, useEffect } from 'react';
import { getStations } from '../services/api';
import {
  setFavouriteStations,
  getFavouriteStations,
  getTimeSavedThreshold,
  setTimeSavedThreshold,
  DEFAULT_TIME_SAVED_THRESHOLD,
} from '../services/storage';
import { Station, StationInfo } from '../types';
import {
  Autocomplete,
  Button,
  Checkbox,
  TextField,
  Typography,
  Box,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import { FilterOptionsState } from '@mui/material/useAutocomplete';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface StationSetupProps {
  onFavouritesChange: (stations: Station[]) => void;
  onClose: () => void;
  stationSuggestions?: StationInfo[];
  timeSavedThreshold?: number;
  onTimeSavedThresholdChange?: (threshold: number) => void;
}

const StationSetup: React.FC<StationSetupProps> = ({
  onFavouritesChange,
  onClose,
  stationSuggestions,
  timeSavedThreshold,
  onTimeSavedThresholdChange,
}) => {
  const [stations, setStations] = useState<Station[]>([]);
  const [selectedStations, setSelectedStations] = useState<Station[]>([]);
  const [loading, setLoading] = useState(true);
  const [inputValue, setInputValue] = useState('');
  const [thresholdInput, setThresholdInput] = useState<string>(() => {
    const fromStorage = typeof getTimeSavedThreshold === 'function' ? getTimeSavedThreshold() : undefined;
    const initial = timeSavedThreshold ?? fromStorage ?? DEFAULT_TIME_SAVED_THRESHOLD ?? 5;
    return initial.toString();
  });

  useEffect(() => {
    const fetchStations = async () => {
      try {
        const cachedStations = localStorage.getItem('cachedStations');
        let allStations: Station[] = [];

        if (cachedStations) {
          allStations = JSON.parse(cachedStations);
        } else {
          const stationData = await getStations();
          allStations = stationData;
          localStorage.setItem('cachedStations', JSON.stringify(allStations));
        }

        // Combine suggestions with all stations, prioritizing suggestions
        const combinedStations: Station[] = [];
        const uniqueCrsCodes = new Set<string>();

        if (stationSuggestions) {
          stationSuggestions.forEach((sug) => {
            const station: Station = { crsCode: sug.crs, stationName: sug.name, lat: '', long: '' };
            if (!uniqueCrsCodes.has(station.crsCode)) {
              combinedStations.push(station);
              uniqueCrsCodes.add(station.crsCode);
            }
          });
        }

        allStations.forEach((station) => {
          if (!uniqueCrsCodes.has(station.crsCode)) {
            combinedStations.push(station);
            uniqueCrsCodes.add(station.crsCode);
          }
        });

        // Sort other stations alphabetically, keeping suggestions at the top
        const sortedOtherStations = combinedStations
          .slice(stationSuggestions?.length || 0)
          .sort((a, b) => a.stationName.localeCompare(b.stationName));

        setStations([
          ...combinedStations.slice(0, stationSuggestions?.length || 0),
          ...sortedOtherStations,
        ]);

        // Pre-populate selected stations
        const favouriteStations = getFavouriteStations();
        setSelectedStations(favouriteStations);
      } catch (error) {
        console.error('Error fetching stations:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStations();
  }, [stationSuggestions]);

  const handleRemoveStation = (crsCode: string) => {
    const newSelected = selectedStations.filter((s) => s.crsCode !== crsCode);
    setSelectedStations(newSelected);
    setFavouriteStations(newSelected);
    onFavouritesChange(newSelected);
  };

  const handleThresholdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const raw = event.target.value;
    setThresholdInput(raw);
    if (raw !== '') {
      const val = parseInt(raw, 10);
      if (!isNaN(val)) {
        const clamped = Math.max(0, val);
        setTimeSavedThreshold(clamped);
        onTimeSavedThresholdChange?.(clamped);
      }
    }
  };

  const handleThresholdBlur = () => {
    if (thresholdInput === '' || isNaN(parseInt(thresholdInput, 10))) {
      setThresholdInput('0');
      setTimeSavedThreshold(0);
      onTimeSavedThresholdChange?.(0);
    } else {
      const clamped = Math.max(0, parseInt(thresholdInput, 10));
      setThresholdInput(clamped.toString());
      setTimeSavedThreshold(clamped);
      onTimeSavedThresholdChange?.(clamped);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      {/* Header with Title and Close Button */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
        <Box>
          <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }} id="station-setup-title">
            Select Favourite Stations
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Choose the stations you want to include in the results.
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="dismiss" edge="end" sx={{ color: 'text.secondary', ml: 1 }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Dedicated Favourites Section */}
      <Box sx={{ mb: 2.5, p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Your Favourites ({selectedStations.length})
          </Typography>
          {selectedStations.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Tap ✕ to remove
            </Typography>
          )}
        </Box>

        {selectedStations.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', py: 0.5 }}>
            No favourite stations selected. Search below to add your regular stations.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedStations.map((station) => (
              <Chip
                key={station.crsCode}
                label={`${station.stationName} (${station.crsCode})`}
                onDelete={() => handleRemoveStation(station.crsCode)}
                color="primary"
                variant="filled"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.8125rem',
                  maxWidth: '100%',
                  '& .MuiChip-deleteIcon': {
                    color: 'inherit',
                    opacity: 0.8,
                    '&:hover': {
                      opacity: 1,
                    },
                  },
                }}
              />
            ))}
          </Box>
        )}
      </Box>

      {/* Search-as-you-type Autocomplete */}
      <Autocomplete
        multiple
        disableClearable
        id="favourite-stations-select"
        options={stations}
        disableCloseOnSelect
        getOptionLabel={(option) => `${option.stationName} (${option.crsCode})`}
        value={selectedStations}
        renderTags={() => null}
        onChange={(event, newValue) => {
          setSelectedStations(newValue);
          setFavouriteStations(newValue);
          onFavouritesChange(newValue);
        }}
        onInputChange={(event, newInputValue) => {
          setInputValue(newInputValue);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && inputValue) {
            const trimmed = inputValue.trim().toLowerCase();
            const filtered = stations.filter(
              (station) =>
                station.crsCode.toLowerCase() === trimmed ||
                station.stationName.toLowerCase() === trimmed
            );

            if (filtered.length === 1 && !selectedStations.some((s) => s.crsCode === filtered[0].crsCode)) {
              const newSelected = [...selectedStations, filtered[0]];
              setSelectedStations(newSelected);
              setFavouriteStations(newSelected);
              onFavouritesChange(newSelected);
              setInputValue('');
            }
          }
        }}
        filterOptions={(options: Station[], state: FilterOptionsState<Station>) => {
          const searchVal = state.inputValue.trim().toLowerCase();
          const suggestedStations = stationSuggestions || [];

          if (!searchVal) {
            return options.slice(0, 20);
          }

          const filteredSuggestions = suggestedStations
            .filter(
              (sug) =>
                sug.crs.toLowerCase().includes(searchVal) ||
                sug.name.toLowerCase().includes(searchVal)
            )
            .map((sug) => ({ crsCode: sug.crs, stationName: sug.name, lat: '', long: '' }));

          const mainFiltered = options.filter(
            (option) =>
              (option.crsCode.toLowerCase().includes(searchVal) ||
                option.stationName.toLowerCase().includes(searchVal)) &&
              !filteredSuggestions.some((fs) => fs.crsCode === option.crsCode)
          );

          const combinedFiltered = [...filteredSuggestions, ...mainFiltered];

          const exactCrsMatch = combinedFiltered.find(
            (option) => option.crsCode.toLowerCase() === searchVal
          );
          const exactNameMatch = combinedFiltered.find(
            (option) => option.stationName.toLowerCase() === searchVal
          );

          const finalFiltered: Station[] = [];
          const finalUniqueCrsCodes = new Set<string>();

          if (exactCrsMatch && !finalUniqueCrsCodes.has(exactCrsMatch.crsCode)) {
            finalFiltered.push(exactCrsMatch);
            finalUniqueCrsCodes.add(exactCrsMatch.crsCode);
          }
          if (exactNameMatch && !finalUniqueCrsCodes.has(exactNameMatch.crsCode)) {
            finalFiltered.push(exactNameMatch);
            finalUniqueCrsCodes.add(exactNameMatch.crsCode);
          }

          combinedFiltered.forEach((station) => {
            if (!finalUniqueCrsCodes.has(station.crsCode)) {
              finalFiltered.push(station);
              finalUniqueCrsCodes.add(station.crsCode);
            }
          });

          return finalFiltered.slice(0, 20);
        }}
        isOptionEqualToValue={(option, value) => option.crsCode === value.crsCode}
        renderOption={(props, option, { selected }) => {
          const { key, ...restProps } = props;
          return (
            <li
              key={key}
              {...restProps}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 12px',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, mr: 1 }}>
                <Checkbox
                  icon={icon}
                  checkedIcon={checkedIcon}
                  style={{ marginRight: 8, padding: 4 }}
                  checked={selected}
                />
                <Typography variant="body2" sx={{ fontWeight: selected ? 600 : 400 }}>
                  {`${option.stationName} (${option.crsCode})`}
                </Typography>
              </Box>
              {selected ? (
                <Chip label="Added" size="small" color="primary" sx={{ height: 20, fontSize: '0.65rem' }} />
              ) : (
                <Chip label="+ Add" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
              )}
            </li>
          );
        }}
        style={{ width: '100%' }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Favourite Stations"
            placeholder="Search station name or 3-letter code (e.g. KGX)..."
            value={inputValue}
            onChange={(event) => setInputValue(event.target.value)}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {inputValue ? (
                    <IconButton
                      size="small"
                      aria-label="clear search"
                      onClick={() => setInputValue('')}
                      edge="end"
                      sx={{ mr: 0.5 }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />

      {/* Fastest Train Recommendation Settings */}
      <Box sx={{ mt: 3, pt: 2.5, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
          Fastest Train Recommendation
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Specify how many minutes earlier a later departing train must arrive to be recommended (highlighted in green).
        </Typography>
        <TextField
          id="time-saved-threshold-input"
          label="Minimum time saved (minutes)"
          type="number"
          size="small"
          value={thresholdInput}
          onChange={handleThresholdChange}
          onBlur={handleThresholdBlur}
          inputProps={{ min: 0, max: 60, step: 1, 'aria-label': 'Minimum time saved in minutes' }}
          fullWidth
          helperText="Set to 0 to always recommend the earliest arriving train regardless of departure time."
        />
      </Box>

      {/* Dialog Footer */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={onClose}
          size="medium"
        >
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default StationSetup;
